import { prop, sort, uniqBy } from 'ramda';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { OrganizationJobPosition } from '@/shared/api/protocol-ts/model/dto_organization_job_position_pb';

import { i18n, NON_DENTAL_POSITIONS, SpecializationOption } from '../config';
import { sortSpecializationOptions } from '../lib';

type UseSpecializationsOptionsParams = {
  isNonDental?: boolean;
  // Positions the staff member already holds. Those outside the organization
  // type (left over from a type change) are returned with `isHiddenInMenu`,
  // so they stay as selected values but cannot be picked again.
  heldPositions?: OrganizationJobPosition[];
};

export const useSpecializationsOptions = (
  params?: UseSpecializationsOptionsParams,
): SpecializationOption[] => {
  const { isNonDental = false, heldPositions = [] } = params ?? {};
  const { formatMessage } = useIntl();

  const dentalOptions = useMemo(
    () => [
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionChiefDentalOfficer
          ],
        ),
        value:
          OrganizationJobPosition.OrganizationJobPositionChiefDentalOfficer,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionTreatmentCoordinator
          ],
        ),
        value:
          OrganizationJobPosition.OrganizationJobPositionTreatmentCoordinator,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionDentalAssistant
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionDentalAssistant,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionDentalTechnician
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionDentalTechnician,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionGeneralDentist
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionGeneralDentist,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionEndodontist
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionEndodontist,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionPeriodontist
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionPeriodontist,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionProsthodontist
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionProsthodontist,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionOrthodontist
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionOrthodontist,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionPediatricDentist
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionPediatricDentist,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition
              .OrganizationJobPositionOralAndMaxillofacialSurgeon
          ],
        ),
        value:
          OrganizationJobPosition.OrganizationJobPositionOralAndMaxillofacialSurgeon,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition
              .OrganizationJobPositionOralAndMaxillofacialRadiologist
          ],
        ),
        value:
          OrganizationJobPosition.OrganizationJobPositionOralAndMaxillofacialRadiologist,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionAestheticDentist
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionAestheticDentist,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionDentalHygienist
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionDentalHygienist,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionOther
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionOther,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionOfficeManager
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionOfficeManager,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionDentalTherapist
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionDentalTherapist,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionImplantologist
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionImplantologist,
      },
      {
        label: formatMessage(
          i18n.specialityType[
            OrganizationJobPosition.OrganizationJobPositionAnesthesiologist
          ],
        ),
        value: OrganizationJobPosition.OrganizationJobPositionAnesthesiologist,
      },
    ],
    [formatMessage],
  );

  const nonDentalOptions = useMemo(
    () =>
      NON_DENTAL_POSITIONS.map((position) => ({
        label: formatMessage(i18n.specialityType[position]),
        value: position,
      })),
    [formatMessage],
  );

  const combinedOptions = useMemo(() => {
    if (!isNonDental) {
      return dentalOptions;
    }

    // Some positions are available to both organization types and therefore
    // appear in both lists — without dedup they would render twice.
    return uniqBy(prop('value'), [...dentalOptions, ...nonDentalOptions]);
  }, [isNonDental, dentalOptions, nonDentalOptions]);

  const typeOptions = sort(sortSpecializationOptions, combinedOptions);

  const outOfTypeOptions = heldPositions
    .filter(
      (position) => !typeOptions.some((option) => option.value === position),
    )
    .map((position) => ({
      label: formatMessage(i18n.specialityType[position]),
      value: position,
      isHiddenInMenu: true,
    }));

  return [...typeOptions, ...outOfTypeOptions];
};
